import { CommonModule } from '@angular/common';
import {
  Component, ElementRef, EventEmitter, Input, NgModule, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DxButtonModule, DxPopupModule, DxScrollViewModule, DxTextBoxModule } from 'devextreme-angular';
import { Observable, Subscription, interval } from 'rxjs';
import {
  LicenseFacility, LicenseIntent, LicenseNextAction, LicenseOutcome, LicenseType, LicenseUploadResult, PendingLicense,
  ReusableLicense,
} from '../../models/license';
import { LicenseDocumentService } from '../../services/license-document.service';
import { SessionService } from '../../services/session.service';
import { isValidDeaNumber } from '../../utils/dea-number';
import { FileViewerModule } from '../file-viewer/file-viewer.component';
import {
  ACTION_LABEL, BOARD_SHEET_STEPS, CopyContext, LABEL, OutcomeCopy, TITLE, WAIT_COPY, formatUploadTime, guidance, outcomeCopy,
} from './license-copy';

type WizardStep =
  | { readonly kind: 'intent' }
  | { readonly kind: 'guidance' }
  | { readonly kind: 'checking' }
  | { readonly kind: 'outcome'; readonly outcome: LicenseOutcome; readonly copy: OutcomeCopy }
  | { readonly kind: 'submitted' }
  | { readonly kind: 'error'; readonly message: string };

const MOBILE_MAX_WIDTH = 576;
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const CHECK_STAGE_MS = 4000;
const SUCCESS_CODES = ['verified', 'changed', 'recorded'];
const EXPIRING_SOON_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

type LicenseHealth = 'active' | 'expiring' | 'expired' | 'missing';

/**
 * The license modal (MDG-359): one guided flow for uploading a state license or
 * DEA registration — pick what you're doing, see what's accepted, upload or
 * photograph it, and fix any problem before it goes to our team.
 */
@Component({
  selector: 'app-license-wizard',
  templateUrl: './license-wizard.component.html',
  styleUrls: ['./license-wizard.component.scss'],
})
export class LicenseWizardComponent implements OnChanges, OnDestroy {
  @Input() facility: LicenseFacility | null = null;
  @Input() licenseType: LicenseType = 'state_license';
  @Input() allowDea = false;
  @Input() facilityVetted = true;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  /** The facility's license on file changed — refresh whatever shows it. */
  @Output() licenseChanged = new EventEmitter<void>();
  /** An upload awaiting review was added or submitted — refresh any "under review" display. */
  @Output() pendingChanged = new EventEmitter<void>();
  @Output() editAddress = new EventEmitter<void>();
  @Output() addFacility = new EventEmitter<void>();

  @ViewChild('photoInput') photoInput?: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  readonly actionLabel = ACTION_LABEL;
  readonly boardSheetSteps = BOARD_SHEET_STEPS;
  readonly waitCopy = WAIT_COPY;
  readonly uploadTime = formatUploadTime;
  /** Outside relaxed tenants a number change goes to our team, not straight onto the facility. */
  changeIsReviewed = false;

  step: WizardStep = { kind: 'intent' };
  intent: LicenseIntent = 'renew';
  newNumber = '';
  numberError = '';
  checkingStage = '';
  isMobile = false;
  busy = false;
  pending: PendingLicense | null = null;
  /** The company's verified copy of the number being uploaded, if it has one (R10). */
  reusable: ReusableLicense | null = null;
  guidanceLines: readonly string[] = [];
  title = '';
  label = '';
  onFileNumber: string | null = null;
  onFileExpiry: string | null = null;
  health: LicenseHealth = 'missing';
  daysLeft = 0;
  onFileDocument: { readonly uri?: string; readonly url?: string } | null = null;
  /** A number with a document behind it — a renewal is possible. */
  hasLicenseOnFile = false;
  viewerUrl: string | null = null;
  viewerName: string | null = null;
  viewerVisible = false;
  /** The file behind the outcome on screen, so the uploader can see what they sent. */
  lastUploadUrl: string | null = null;
  /** The failed upload a retry replaces, and that Submit for review sends. */
  private pendingDocumentId: number | null = null;
  private readonly subs = new Subscription();
  private checkingSub: Subscription | null = null;

  constructor(private licenseService: LicenseDocumentService, private sessionService: SessionService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue) {
      this.open();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.checkingSub?.unsubscribe();
  }

  setLicenseType(type: LicenseType): void {
    this.licenseType = type;
    this.open();
  }

  chooseIntent(intent: LicenseIntent): void {
    this.intent = intent;
    this.numberError = '';
    if (intent !== 'change') {
      this.guidanceLines = guidance(this.copyContext());
      this.step = { kind: 'guidance' };
    }
  }

  continueWithNewNumber(): void {
    const number = this.newNumber.trim();
    if (!number) {
      this.numberError = `Enter the new ${this.label} number.`;
      return;
    }
    if (this.licenseType === 'dea_license' && !isValidDeaNumber(number)) {
      this.numberError = 'That isn\'t a valid DEA number. Check for typos — it is two letters (or a letter and 9) followed by seven digits.';
      return;
    }
    this.numberError = '';
    // The upload must now match the number being changed to, not the one on file.
    this.guidanceLines = guidance({ ...this.copyContext(), onFileNumber: number });
    this.checkReusable(number);
    this.step = { kind: 'guidance' };
  }

  useReusable(): void {
    if (!this.facility || !this.reusable?.number) {
      return;
    }
    this.startChecking();
    this.handleResult(this.licenseService.reuse(this.facility.id, this.reusable.number));
  }

  pickPhoto(): void {
    this.photoInput?.nativeElement.click();
  }

  pickFile(): void {
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      this.step = { kind: 'error', message: 'That file is larger than 20 MB. Please upload a smaller photo or file.' };
      return;
    }
    this.upload(file);
  }

  runAction(action: LicenseNextAction): void {
    switch (action) {
      case 'upload_different':
      case 'try_again':
        this.step = { kind: 'guidance' };
        return;
      case 'submit_review':
        this.submitForReview();
        return;
      case 'confirm_change':
        this.recheckAsChange();
        return;
      case 'use_document_number':
        this.recheckAsChange(this.documentNumber());
        return;
      case 'edit_address':
        this.leaveToFix(this.editAddress);
        return;
      case 'add_facility':
        this.leaveToFix(this.addFacility);
        return;
    }
  }

  /** Drop the banner's unsent upload: nothing was emailed, so nothing to undo. */
  withdrawPendingFromHeader(): void {
    if (!this.pending) {
      return;
    }
    this.busy = true;
    this.subs.add(this.licenseService.withdraw(this.pending.id).subscribe({
      next: () => {
        this.busy = false;
        this.loadPending();
        this.pendingChanged.emit();
      },
      error: () => {
        this.busy = false;
        this.showError(null);
      },
    }));
  }

  /** The banner's upload is an earlier one: submit it without touching the current flow. */
  submitPendingFromHeader(): void {
    if (!this.pending) {
      return;
    }
    this.busy = true;
    this.subs.add(this.licenseService.submit(this.pending.id).subscribe({
      next: () => {
        this.busy = false;
        this.loadPending();
        this.pendingChanged.emit();
      },
      error: () => {
        this.busy = false;
        this.showError(null);
      },
    }));
  }

  /** Show a stored file in the viewer popup instead of a new browser tab. */
  openViewer(url: string | null | undefined, name: string | null | undefined): void {
    if (!url) {
      return;
    }
    this.viewerUrl = url;
    this.viewerName = name ?? null;
    this.viewerVisible = true;
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  /** dx-popup onHiding: a failed upload stays unsent; the banner and the
   *  Facilities page offer Submit for review or Withdraw. */
  onHiding(): void {
    this.checkingSub?.unsubscribe();
    if (this.pendingDocumentId) {
      this.pendingChanged.emit();
    }
    this.pendingDocumentId = null;
    if (this.visible) {
      this.close();
    }
  }

  private open(): void {
    this.isMobile = window.innerWidth < MOBILE_MAX_WIDTH;
    this.pendingDocumentId = null;
    this.lastUploadUrl = null;
    this.newNumber = '';
    this.numberError = '';
    this.label = LABEL[this.licenseType];
    this.title = TITLE[this.licenseType];
    this.onFileNumber = (this.licenseType === 'state_license' ? this.facility?.state_license : this.facility?.dea_number) || null;
    this.onFileExpiry = (this.licenseType === 'state_license'
      ? this.facility?.state_license_expiration : this.facility?.dea_expiration) || null;
    this.onFileDocument = this.facility?.documents?.find(doc => doc.type === this.licenseType && !doc.expired) ?? null;
    this.changeIsReviewed = !this.licenseService.isRelaxedTenant();
    this.setHealth();
    this.guidanceLines = guidance(this.copyContext());
    // A number with no document (a facility just created from a license search)
    // is a first upload, not a renewal.
    this.hasLicenseOnFile = !!this.onFileNumber && !!this.onFileDocument;
    this.intent = this.hasLicenseOnFile ? 'renew' : 'first';
    // DEA has nothing to choose: renewal is its only path.
    this.step = this.hasLicenseOnFile && this.licenseType === 'state_license' ? { kind: 'intent' } : { kind: 'guidance' };
    this.reusable = null;
    if (!this.hasLicenseOnFile && this.onFileNumber) {
      this.checkReusable(this.onFileNumber);
    }
    this.loadPending();
  }

  private setHealth(trustServerFlag = true): void {
    if (!this.onFileNumber || !this.onFileExpiry) {
      this.health = 'missing';
      return;
    }
    const expiredPerServer = trustServerFlag && (this.licenseType === 'state_license'
      ? this.facility?.state_license_expired : this.facility?.dea_license_expired);
    // The API sends dates in more than one format (ISO, HTTP-date); Date.parse reads both.
    const expiresAt = Date.parse(this.onFileExpiry);
    this.daysLeft = Number.isNaN(expiresAt) ? 0 : Math.ceil((expiresAt - Date.now()) / DAY_MS);
    if (expiredPerServer || (!Number.isNaN(expiresAt) && this.daysLeft <= 0)) {
      this.health = 'expired';
      return;
    }
    this.health = !Number.isNaN(expiresAt) && this.daysLeft <= EXPIRING_SOON_DAYS ? 'expiring' : 'active';
  }

  private loadPending(): void {
    this.pending = null;
    if (!this.facility) {
      return;
    }
    this.subs.add(this.licenseService.pending(this.facility.id).subscribe(items => {
      this.pending = items.find(item => item.license_type === this.licenseType) ?? null;
    }));
  }

  /** Only relaxed tenants share a license across facilities. */
  private checkReusable(number: string): void {
    this.reusable = null;
    if (!this.facility || this.licenseType !== 'state_license' || !this.licenseService.isRelaxedTenant()) {
      return;
    }
    this.subs.add(this.licenseService.reusable(this.facility.id, number).subscribe(result => {
      this.reusable = result.available ? result : null;
    }));
  }

  private upload(file: File): void {
    if (!this.facility) {
      return;
    }
    this.startChecking();
    this.handleResult(this.licenseService.upload({
      file,
      facilityId: this.facility.id,
      companyId: this.facility.company_id,
      licenseType: this.licenseType,
      intent: this.intent,
      newLicenseNumber: this.intent === 'change' ? this.newNumber.trim() : undefined,
      // A retry replaces this session's failed upload, or else an earlier unsent one
      // (submitted uploads stay with our team; the server refuses to replace those).
      replacesDocumentId: this.pendingDocumentId ?? (this.pending && !this.pending.submitted ? this.pending.id : null),
    }));
  }

  private recheckAsChange(number?: string): void {
    if (!this.pendingDocumentId) {
      return;
    }
    this.intent = 'change';
    this.startChecking();
    this.handleResult(this.licenseService.change(this.pendingDocumentId, number));
  }

  private handleResult(request$: Observable<LicenseUploadResult>): void {
    this.subs.add(request$.subscribe({
      next: result => this.showOutcome(result),
      error: response => this.showError(response?.error?.error),
    }));
  }

  private showOutcome(result: LicenseUploadResult): void {
    this.stopChecking();
    // A re-check ("Yes, change to…") returns no file: it is about the one already shown.
    this.lastUploadUrl = result.url ?? this.lastUploadUrl;
    const outcome = this.withHandledActions(result.license_feedback);
    const succeeded = SUCCESS_CODES.includes(outcome.code);
    this.pendingDocumentId = succeeded ? null : result.id;
    this.step = { kind: 'outcome', outcome, copy: outcomeCopy(outcome, this.copyContext(), this.facilityVetted) };
    this.pendingChanged.emit();
    this.loadPending();
    if (succeeded) {
      this.showNewLicenseOnFile(result);
      // The header, cart and facility pages re-read the facility's license on this trigger.
      this.sessionService.primaryFacilityUpdatedTrigger.next(true);
      this.licenseChanged.emit();
    }
  }

  /** The status header still holds the license from when the modal opened; show the new one. */
  private showNewLicenseOnFile(result: LicenseUploadResult): void {
    const data = result.license_feedback.data;
    // A change brings the new number; a first upload may have had none on file.
    this.onFileNumber = data.replaced?.to ?? this.onFileNumber ?? data.uploaded ?? null;
    this.onFileExpiry = data.expiry ?? this.onFileExpiry;
    this.onFileDocument = result.url ? { uri: result.uri, url: result.url } : this.onFileDocument;
    this.hasLicenseOnFile = !!this.onFileNumber && !!this.onFileDocument;
    // The facility input's expired flag predates this upload.
    this.setHealth(false);
  }

  private showError(error: unknown): void {
    this.stopChecking();
    const message = error === 'invalid_dea_number'
      ? 'That isn\'t a valid DEA number. Check it and try again.'
      : 'The upload didn\'t go through. Please try again.';
    this.step = { kind: 'error', message };
  }

  private submitForReview(): void {
    const documentId = this.pendingDocumentId;
    if (!documentId) {
      return;
    }
    // Cleared now, not on the response: closing mid-request must not submit it twice.
    this.pendingDocumentId = null;
    this.busy = true;
    this.subs.add(this.licenseService.submit(documentId).subscribe({
      next: () => {
        this.busy = false;
        this.step = { kind: 'submitted' };
        this.loadPending();
        this.pendingChanged.emit();
      },
      error: () => {
        this.busy = false;
        this.pendingDocumentId = documentId;
        this.showError(null);
      },
    }));
  }

  /** Drop self-solve actions the host page has no way to carry out. */
  private withHandledActions(outcome: LicenseOutcome): LicenseOutcome {
    const unhandled: LicenseNextAction[] = [
      ...(this.editAddress.observers.length && this.licenseService.canEditFacilityAddress() ? [] : ['edit_address' as const]),
      ...(this.addFacility.observers.length ? [] : ['add_facility' as const]),
    ];
    return { ...outcome, next_actions: outcome.next_actions.filter(action => !unhandled.includes(action)) };
  }

  private leaveToFix(destination: EventEmitter<void>): void {
    this.close();
    destination.emit();
  }

  private documentNumber(): string | undefined {
    if (this.step.kind !== 'outcome') {
      return undefined;
    }
    return (this.step.outcome.data.uploaded ?? '').replace(/[\s./-]/g, '') || undefined;
  }

  private startChecking(): void {
    const stages = this.licenseType === 'state_license'
      ? ['Reading the document…', 'Checking state records…', 'Checking the address…']
      : ['Reading the document…', 'Checking DEA records…'];
    this.checkingStage = stages[0];
    this.step = { kind: 'checking' };
    this.checkingSub?.unsubscribe();
    this.checkingSub = interval(CHECK_STAGE_MS).subscribe(tick => {
      this.checkingStage = stages[Math.min(tick + 1, stages.length - 1)];
    });
  }

  private stopChecking(): void {
    this.checkingSub?.unsubscribe();
    this.checkingSub = null;
  }

  private copyContext(): CopyContext {
    return {
      licenseType: this.licenseType,
      relaxedTenant: this.licenseService.isRelaxedTenant(),
      stateOnlyAddress: this.licenseService.isRelaxedTenant(),
      onFileNumber: this.onFileNumber,
      facilityState: this.facility?.delivery_address?.state ?? null,
    };
  }
}

@NgModule({
  imports: [CommonModule, FormsModule, DxButtonModule, DxPopupModule, DxScrollViewModule, DxTextBoxModule, FileViewerModule],
  declarations: [LicenseWizardComponent],
  exports: [LicenseWizardComponent],
})
export class LicenseWizardModule {}
